import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';

const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string) || 'http://app.localtest.me:3001';
const LOAD_TIMEOUT_MS = 8000;

const PlaybackEditorEmbed: React.FC = () => {
  const { playbackId } = useParams<{ playbackId: string }>();
  const { currentLanguage, navigateWithLang } = useLanguage();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const savedRef = useRef(false);

  const isCreate = !playbackId || playbackId === 'new';

  const editorUrl = useMemo(() => {
    const base = APP_ORIGIN.replace(/\/$/, '');
    const params = new URLSearchParams({ embed: '1' });
    if (!isCreate) params.set('playbackId', playbackId);
    return `${base}/${currentLanguage}/playback-editor?${params.toString()}`;
  }, [currentLanguage, isCreate, playbackId]);

  useEffect(() => {
    setLoading(true);
    savedRef.current = false;
    const timeout = window.setTimeout(() => setLoading(false), LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [editorUrl]);

  useEffect(() => {
    const expectedOrigin = (() => {
      try {
        return new URL(APP_ORIGIN).origin;
      } catch {
        return '';
      }
    })();

    const onMessage = (event: MessageEvent) => {
      if (expectedOrigin && event.origin !== expectedOrigin) return;
      if (event.data?.source !== 'tms-playback-editor') return;
      if (event.data.type === 'playback-editor-ready') {
        setLoading(false);
        return;
      }
      if (event.data.type === 'playback-saved') {
        savedRef.current = true;
        navigateWithLang('/playbacks');
        return;
      }
      if (event.data.type === 'playback-editor-closed' && !savedRef.current) {
        navigateWithLang('/playbacks');
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigateWithLang]);

  return (
    <ContentPageLayout>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Typography variant="h5" color="primary" fontWeight="bold">
          {isCreate ? t('playbackEditor.create') : t('playbackEditor.edit')}
        </Typography>
        <Button variant="outlined" onClick={() => navigateWithLang('/playbacks')}>
          {t('playbackEditor.back')}
        </Button>
      </Box>
      <Box sx={{ position: 'relative', minHeight: { xs: '70vh', md: 'calc(100vh - 14em)' } }}>
        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t('loading')}
            </Typography>
          </Box>
        ) : null}
        <Box
          component="iframe"
          title={t('playbackEditor.iframeTitle')}
          src={editorUrl}
          onLoad={() => setLoading(false)}
          onError={() => setLoading(false)}
          sx={{
            width: '100%',
            minHeight: { xs: '70vh', md: 'calc(100vh - 14em)' },
            height: { xs: '70vh', md: 'calc(100vh - 14em)' },
            border: 0,
            bgcolor: 'background.paper',
            display: 'block',
          }}
        />
      </Box>
    </ContentPageLayout>
  );
};

export default PlaybackEditorEmbed;
